import type * as React from 'react';

import { type TableColumnDefinition, type TableColumnType, table } from '@gitbook/doc-core';
import { PaletteButton, type PaletteDef } from '@gitbook/react-uikit';
import type { EditorContainer } from '@gitbook/slate-react';
import { confirm, toast } from '@gitbook/spine-react';

import { createColumnDefsPalette, definitionSpecs } from '../../ColumnDefinitions';

interface ChangeTypeButtonProps {
    nodeKey: string;
    editor: EditorContainer;
    columnDefinition: TableColumnDefinition;
}

export function ChangeTypeButton(props: ChangeTypeButtonProps): React.ReactElement {
    const { nodeKey, editor, columnDefinition } = props;

    const updateDefinition = (definition: TableColumnDefinition) => {
        editor.change((change) => {
            table.setColumnDefinition(change, nodeKey, columnDefinition.id, definition);
        });
    };

    const generatePalette = (): PaletteDef => {
        return createColumnDefsPalette(onCompleteChangeType, {
            selectedType: columnDefinition.type,
        });
    };

    const onCompleteChangeType = (columnType: TableColumnType) => {
        void confirm({
            title: 'Change column type',
            description: `You are about to change this column type from ${columnDefinition.type} to ${columnType}. We will try to convert any value automatically, but you may lose data if we cannot convert them.`,
            confirmLabel: 'Change',
            onConfirm: async (_event, { preventClose }) => {
                await toast.catch(
                    () => {
                        updateDefinition(table.changeColumnType(columnDefinition, columnType));
                        toast.success(`Column type changed to ${columnType}`);
                    },
                    'Error changing column type',
                    { onReject: preventClose }
                );
            },
        });
    };

    return (
        <PaletteButton
            leadingIcon={definitionSpecs[columnDefinition.type].icon}
            label={definitionSpecs[columnDefinition.type].title}
            title="Change value type"
            kind="secondary"
            iconOnly
            palette={generatePalette}
            className="transition group-hover:opacity-100"
        />
    );
}
